import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Box,
    IconButton,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import purchaseApi from "../../api/purchaseApi";
import ConfirmModal from "../../components/common/ConfirmModal";
import AppDropdown from "../../components/AppDropdown";
import "../../style/ToastNotifications.css";
import "./PurchaseOrderList.css";

function PurchaseOrderList() {
    const navigate = useNavigate();

    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(true);
    // const [loading, setLoading] = useState(true);


    // =========================
    // SEARCH / FILTER STATES
    // =========================
    const [searchFilters, setSearchFilters] = useState({
        vendorId: "",
        purchaseNumber: "",
        purchaseType: "",
        productId: "",
        fromDate: "",
        toDate: "",
    });

    const [showSearch, setShowSearch] = useState(false);

    // =========================
    // POPUP STATES
    // =========================
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: "Confirm Delete",
        message: "Are you sure you want to delete this purchase order?",
        type: "warning",
        confirmText: "Delete",
        cancelText: "Cancel",
        onConfirm: null,
    });

    // =========================
    // GET PURCHASE ORDERS
    // =========================
    const fetchPurchaseOrders = async () => {
        setIsLoading(true);

        try {
            const response = await purchaseApi.getAll();

            let orders = [];

            if (response && Array.isArray(response.data)) {
                orders = response.data;
            } else if (Array.isArray(response)) {
                orders = response;
            }

            setPurchaseOrders(orders);
            setFilteredOrders(orders);

            // Save to localStorage as backup
            localStorage.setItem(
                "purchaseOrders",
                JSON.stringify(orders)
            );
        } catch (error) {
            console.error("Fetch purchase orders error:", error);

            // =========================
            // FALLBACK TO LOCAL STORAGE
            // =========================
            try {
                const savedOrders =
                    localStorage.getItem("purchaseOrders");

                if (savedOrders) {
                    const parsedOrders = JSON.parse(savedOrders);

                    if (Array.isArray(parsedOrders)) {
                        setPurchaseOrders(parsedOrders);
                        setFilteredOrders(parsedOrders);

                        toast.info(
                            "Showing cached purchase orders."
                        );

                        return;
                    }
                }
            } catch (storageError) {
                console.error(
                    "Load localStorage error:",
                    storageError
                );
            }

            setPurchaseOrders([]);
            setFilteredOrders([]);

            const toastId = toast.error(
                "Failed to fetch purchase orders. Please try again.",
                {
                    autoClose: false,
                    closeOnClick: false,
                    pauseOnHover: false,
                    pauseOnFocusLoss: false,
                    draggable: false,
                }
            );

            setTimeout(() => {
                toast.dismiss(toastId);
            }, 3000);
        } finally {
            setIsLoading(false);
        }
    };
    const handleSearch = () => {
        const {
            purchaseNumber,
            purchaseType,
            vendorId,
            productId,
            fromDate,
            toDate,
        } = searchFilters;

        const filtered = purchaseOrders.filter((order) => {
            // Purchase Number
            const orderPurchaseNumber = String(
                order.purchaseOrderNumber ||
                order.poNumber ||
                order.purchaseNumber ||
                ""
            ).trim().toLowerCase();

            const matchPurchaseNumber =
                !purchaseNumber ||
                orderPurchaseNumber.includes(
                    purchaseNumber.trim().toLowerCase()
                );

            // Purchase Type
            const orderPurchaseType = String(
                order.purchaseType ||
                order.type ||
                ""
            ).trim().toLowerCase();

            const matchPurchaseType =
                !purchaseType ||
                orderPurchaseType === purchaseType.trim().toLowerCase();

            // Vendor ID - exact match
            const orderVendorId = String(
                order.vendorId || ""
            ).trim().toLowerCase();

            const matchVendor =
                !vendorId ||
                orderVendorId === vendorId.trim().toLowerCase();

            // Product ID - search inside items
            const matchProduct =
                !productId ||
                (order.items || []).some((item) => {
                    const itemProductId = String(
                        item.goodsItemId ||
                        item.productId ||
                        item.itemId ||
                        ""
                    ).trim().toLowerCase();

                    return (
                        itemProductId ===
                        productId.trim().toLowerCase()
                    );
                });

            // Purchase Date
            const orderDate = new Date(
                order.purchaseOrderDate ||
                order.poDate ||
                order.purchase_date
            );

            let matchFromDate = true;
            let matchToDate = true;

            if (fromDate && !isNaN(orderDate)) {
                const from = new Date(fromDate);
                from.setHours(0, 0, 0, 0);

                matchFromDate = orderDate >= from;
            }

            if (toDate && !isNaN(orderDate)) {
                const to = new Date(toDate);
                to.setHours(23, 59, 59, 999);

                matchToDate = orderDate <= to;
            }

            return (
                matchPurchaseNumber &&
                matchPurchaseType &&
                matchVendor &&
                matchProduct &&
                matchFromDate &&
                matchToDate
            );
        });

        setFilteredOrders(filtered);
        toast.success(
            `${filtered.length} purchase order${filtered.length === 1 ? "" : "s"} found`
        );
    };


    // if (filtered.length === 0) {
    //     toast.warning("No purchase orders found.");
    // } else {
    //     toast.success(
    //         `${filtered.length} purchase order${filtered.length === 1 ? "" : "s"} found`
    //     );
    // }


    // =========================
    // CLEAR SEARCH
    // =========================
    const clearSearch = () => {
        setSearchFilters({
            vendorId: "",
            purchaseNumber: "",
            purchaseType: "",
            productId: "",
            fromDate: "",
            toDate: "",
        });

        setFilteredOrders(purchaseOrders);
    };

    // =========================
    // FETCH ON PAGE LOAD
    // =========================
    useEffect(() => {
        fetchPurchaseOrders();
    }, []);

    // =========================
    // DELETE
    // =========================
    const handleDelete = async (id) => {
        setConfirmModal({
            isOpen: true,
            title: "Delete Purchase Order",
            message:
                "Are you sure you want to delete this purchase order? This action cannot be undone.",
            type: "warning",
            confirmText: "Delete",
            cancelText: "Cancel",

            onConfirm: async () => {
                try {
                    await purchaseApi.delete(id);

                    const updatedOrders =
                        purchaseOrders.filter(
                            (order) => order.id !== id
                        );

                    setPurchaseOrders(updatedOrders);
                    setFilteredOrders(updatedOrders);

                    localStorage.setItem(
                        "purchaseOrders",
                        JSON.stringify(updatedOrders)
                    );

                    setConfirmModal({
                        isOpen: false,
                        title: "Confirm Delete",
                        message:
                            "Are you sure you want to delete this purchase order?",
                        type: "warning",
                        confirmText: "Delete",
                        cancelText: "Cancel",
                        onConfirm: null,
                    });

                    toast.success(
                        "Purchase order deleted successfully."
                    );
                } catch (error) {
                    console.error(
                        "Delete Error:",
                        error
                    );

                    toast.error(
                        "Failed to delete purchase order."
                    );

                    setConfirmModal({
                        isOpen: false,
                        title: "Confirm Delete",
                        message:
                            "Are you sure you want to delete this purchase order?",
                        type: "warning",
                        confirmText: "Delete",
                        cancelText: "Cancel",
                        onConfirm: null,
                    });
                }
            },
        });
    };

    // =========================
    // VIEW
    // =========================
    const handleView = (po) => {
        setSelectedOrder(po);
        setShowViewModal(true);
    };

    // =========================
    // EDIT
    // =========================
    const handleEdit = (id) => {
        navigate(
            `/dashboard/purchases/orders/edit/${id}`
        );
    };

    // =========================
    // CLOSE VIEW MODAL
    // =========================
    const closeViewModal = () => {
        setShowViewModal(false);
        setSelectedOrder(null);
    };

    // =========================
    // CLOSE CONFIRM MODAL
    // =========================
    const closeConfirmModal = () => {
        setConfirmModal({
            isOpen: false,
            title: "Confirm Delete",
            message:
                "Are you sure you want to delete this purchase order?",
            type: "warning",
            confirmText: "Delete",
            cancelText: "Cancel",
            onConfirm: null,
        });
    };

    // =========================
    // RETURN
    // =========================
    return (
        <div className="purchase-list">

            {/* =========================
                CONFIRM DELETE MODAL
            ========================= */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                onConfirm={() => {
                    if (confirmModal.onConfirm) {
                        confirmModal.onConfirm();
                    }
                }}
                onCancel={closeConfirmModal}
            />

            {/* =========================
                VIEW PURCHASE ORDER POPUP
            ========================= */}
            {showViewModal &&
                selectedOrder &&
                createPortal(
                    <div
                        className="po-modal-overlay"
                        onClick={closeViewModal}
                    >
                        <div
                            className="po-view-modal"
                            onClick={(e) =>
                                e.stopPropagation()
                            }
                        >

                            {/* MODAL HEADER */}
                            <div className="po-modal-header">

                                <div className="po-modal-title-area">

                                    <div className="po-modal-icon">
                                        📋
                                    </div>

                                    <div>
                                        <h2>
                                            Purchase Order Details
                                        </h2>

                                        <p>
                                            Complete purchase order
                                            information
                                        </p>
                                    </div>

                                </div>

                                <button
                                    className="po-modal-close"
                                    onClick={closeViewModal}
                                >
                                    ×
                                </button>

                            </div>

                            {/* MODAL CONTENT */}
                            <div className="po-modal-content">

                                {/* =========================
                                    BASIC INFORMATION
                                ========================= */}
                                <div className="po-info-section">

                                    <div className="po-section-title">
                                        <span>📄</span>
                                        <h3>
                                            Basic Information
                                        </h3>
                                    </div>

                                    <div className="po-info-grid">

                                        <div className="po-info-field">
                                            <label>
                                                Vendor Name
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.vendorName ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Vendor Email
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.vendorEmail ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Purchase Order Number
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.purchaseOrderNumber ||
                                                    selectedOrder.poNumber ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Reference Number
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.referenceNumber ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Purchase Order Date
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.purchaseOrderDate ||
                                                    selectedOrder.poDate ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Delivery Date
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.deliveryDate ||
                                                    selectedOrder.delivery_date ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                    </div>
                                </div>

                                {/* =========================
                                    VENDOR ADDRESS
                                ========================= */}
                                <div className="po-info-section">

                                    <div className="po-section-title">
                                        <span>📍</span>

                                        <h3>
                                            Vendor Address
                                        </h3>
                                    </div>

                                    <div className="po-info-grid">

                                        <div className="po-info-field full">
                                            <label>
                                                Address
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.vendorAddress ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                    </div>
                                </div>

                                {/* =========================
                                    DELIVERY INFORMATION
                                ========================= */}
                                <div className="po-info-section">

                                    <div className="po-section-title">
                                        <span>🚚</span>

                                        <h3>
                                            Delivery Information
                                        </h3>
                                    </div>

                                    <div className="po-info-grid">

                                        <div className="po-info-field">
                                            <label>
                                                Destination Address
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.destinationAddress ||
                                                    selectedOrder.destination_address ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Shipment Preference
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.shipmentPreference ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Payment Terms
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.paymentTerms ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                    </div>
                                </div>

                                {/* =========================
                                    TAX SETTINGS
                                ========================= */}
                                <div className="po-info-section">

                                    <div className="po-section-title">
                                        <span>💰</span>

                                        <h3>
                                            Tax Settings
                                        </h3>
                                    </div>

                                    <div className="po-info-grid">

                                        <div className="po-info-field">
                                            <label>
                                                Tax Type
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.taxType ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                        <div className="po-info-field">
                                            <label>
                                                Tax Rate
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.taxRate ||
                                                    "-"
                                                }
                                            </span>
                                        </div>

                                    </div>
                                </div>

                                {/* =========================
                                    ITEMS
                                ========================= */}
                                <div className="po-info-section">

                                    <div className="po-section-title">
                                        <span>📦</span>

                                        <h3>
                                            Items
                                        </h3>
                                    </div>

                                    {selectedOrder.items &&
                                        selectedOrder.items.length > 0 ? (
                                        <div className="po-items-table-wrapper">

                                            <table className="po-items-table">

                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Item</th>
                                                        <th>Description</th>
                                                        <th>Quantity</th>
                                                        <th>Unit</th>
                                                        <th>Rate</th>
                                                        <th>Discount</th>
                                                        <th>Tax</th>
                                                        <th>Amount</th>
                                                    </tr>
                                                </thead>

                                                <tbody>

                                                    {selectedOrder.items.map(
                                                        (
                                                            item,
                                                            index
                                                        ) => (
                                                            <tr
                                                                key={
                                                                    index
                                                                }
                                                            >

                                                                <td>
                                                                    {
                                                                        index +
                                                                        1
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.item ||
                                                                        item.productName ||
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.description ||
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.quantity ??
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.unit ||
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.rate ??
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.discount ??
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.tax ||
                                                                        "-"
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        item.amount ??
                                                                        "-"
                                                                    }
                                                                </td>

                                                            </tr>
                                                        )
                                                    )}

                                                </tbody>

                                            </table>

                                        </div>
                                    ) : (
                                        <div className="po-no-items">
                                            No items found
                                        </div>
                                    )}

                                </div>

                                {/* =========================
                                    ADDITIONAL INFORMATION
                                ========================= */}
                                <div className="po-info-section">

                                    <div className="po-section-title">
                                        <span>📝</span>

                                        <h3>
                                            Additional Information
                                        </h3>
                                    </div>

                                    <div className="po-info-grid">

                                        <div className="po-info-field full">

                                            <label>
                                                Notes
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.notes ||
                                                    "-"
                                                }
                                            </span>

                                        </div>

                                        <div className="po-info-field full">

                                            <label>
                                                Terms & Conditions
                                            </label>

                                            <span>
                                                {
                                                    selectedOrder.termsAndConditions ||
                                                    selectedOrder.terms_and_conditions ||
                                                    "-"
                                                }
                                            </span>

                                        </div>

                                    </div>

                                </div>

                            </div>

                            {/* =========================
                                MODAL FOOTER
                            ========================= */}
                            <div className="po-modal-footer">

                                <button
                                    className="po-edit-modal-btn"
                                    onClick={() => {
                                        closeViewModal();

                                        navigate(
                                            `/dashboard/purchases/orders/edit/${selectedOrder.id}`
                                        );
                                    }}
                                >
                                    Edit Purchase Order
                                </button>

                                <button
                                    className="po-close-modal-btn"
                                    onClick={closeViewModal}
                                >
                                    Close
                                </button>

                            </div>

                        </div>
                    </div>,
                    document.body
                )}

            {/* =========================
                PAGE HEADER
            ========================= */}
            <div className="purchase-list-header">

                <div className="purchase-header-left">

                    <div
                        className="purchase-header-icon"
                        aria-hidden="true"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M7 4.75H13.5L18.25 9.5V18.25C18.25 18.7 17.95 19 17.5 19H7C6.55 19 6.25 18.7 6.25 18.25V5.5C6.25 5.05 6.55 4.75 7 4.75Z"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />

                            <path
                                d="M13.5 4.75V9.5H18.25"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />

                            <path
                                d="M9 12.5H15"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                            />

                            <path
                                d="M9 15.5H15"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>

                    <h1>
                        Purchase Orders
                    </h1>

                </div>

                <div className="purchase-header-actions">

                    <button
                        className="add-btn"
                        onClick={() =>
                            navigate(
                                "/dashboard/purchases/orders/new"
                            )
                        }
                    >
                        + Add Purchase Order
                    </button>

                </div>

            </div>

            {/* =========================
                SEARCH BUTTON
            ========================= */}
            {!showSearch && (
                <div className="purchase-search-button-container">

                    <button
                        className="search-btn"
                        onClick={() =>
                            setShowSearch(true)
                        }
                    >

                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                                marginRight: "8px",
                            }}
                        >
                            <circle
                                cx="11"
                                cy="11"
                                r="7"
                            />

                            <line
                                x1="16.65"
                                y1="16.65"
                                x2="21"
                                y2="21"
                            />
                        </svg>

                        Show Filters

                    </button>

                </div>
            )}

            {/* =========================
                SEARCH PANEL
            ========================= */}
            {showSearch && (
                <div className="purchase-search-panel">

                    <div className="search-panel-header">

                        <div
                            className="search-panel-title"
                            onClick={() =>
                                setShowSearch(false)
                            }
                        >

                            <div className="search-panel-icon">

                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="17"
                                    height="17"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle
                                        cx="11"
                                        cy="11"
                                        r="7"
                                    />

                                    <line
                                        x1="16.65"
                                        y1="16.65"
                                        x2="21"
                                        y2="21"
                                    />
                                </svg>

                            </div>

                            <span>
                                Filters
                            </span>

                            <svg
                                className="search-panel-chevron"
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="18 15 12 9 6 15" />
                            </svg>

                        </div>

                        <button
                            className="search-clear-filters-btn"
                            onClick={clearSearch}
                            disabled={
                                !searchFilters.purchaseNumber &&
                                !searchFilters.purchaseType &&
                                !searchFilters.vendorId &&
                                !searchFilters.productId &&
                                !searchFilters.fromDate &&
                                !searchFilters.toDate
                            }
                        >

                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    marginRight: "6px",
                                }}
                            >
                                <line
                                    x1="18"
                                    y1="6"
                                    x2="6"
                                    y2="18"
                                />

                                <line
                                    x1="6"
                                    y1="6"
                                    x2="18"
                                    y2="18"
                                />
                            </svg>

                            Clear Filters

                        </button>

                    </div>

                    <div className="search-grid">

                        {/* PURCHASE NUMBER */}
                        <div className="search-field">

                            <label>
                                Purchase Number
                            </label>

                            <input
                                type="text"
                                value={
                                    searchFilters.purchaseNumber
                                }
                                onChange={(e) =>
                                    setSearchFilters({
                                        ...searchFilters,
                                        purchaseNumber:
                                            e.target.value,
                                    })
                                }
                                placeholder="e.g., IN-5-20260820-1"
                            />

                        </div>

                        {/* PURCHASE TYPE */}
                        <div className="search-field">

                            <label>
                                Purchase Type
                            </label>

                            <AppDropdown
                                value={
                                    searchFilters.purchaseType ||
                                    "ALL"
                                }
                                options={[
                                    {
                                        value: "ALL",
                                        label: "All Types",
                                    },
                                    {
                                        value: "INWARD",
                                        label: "INWARD",
                                    },
                                    {
                                        value: "OUTWARD",
                                        label: "OUTWARD",
                                    },
                                ]}
                                onChange={(value) =>
                                    setSearchFilters({
                                        ...searchFilters,
                                        purchaseType:
                                            value === "ALL"
                                                ? ""
                                                : value,
                                    })
                                }
                                placeholder="All Types"
                                position="below"
                            />

                        </div>

                        {/* VENDOR ID */}
                        <div className="search-field">

                            <label>
                                Vendor ID
                            </label>

                            <input
                                type="text"
                                value={
                                    searchFilters.vendorId
                                }
                                onChange={(e) =>
                                    setSearchFilters({
                                        ...searchFilters,
                                        vendorId:
                                            e.target.value,
                                    })
                                }
                                placeholder="Vendor ID"
                            />

                        </div>

                        {/* PRODUCT ID */}
                        <div className="search-field">

                            <label>
                                Product ID
                            </label>

                            <input
                                type="text"
                                value={
                                    searchFilters.productId
                                }
                                onChange={(e) =>
                                    setSearchFilters({
                                        ...searchFilters,
                                        productId:
                                            e.target.value,
                                    })
                                }
                                placeholder="Product ID"
                            />

                        </div>

                        {/* FROM DATE */}
                        <div className="search-field">

                            <label>
                                From Date
                            </label>

                            <input
                                type="date"
                                value={
                                    searchFilters.fromDate
                                }
                                onChange={(e) =>
                                    setSearchFilters({
                                        ...searchFilters,
                                        fromDate:
                                            e.target.value,
                                    })
                                }
                            />

                        </div>

                        {/* TO DATE */}
                        <div className="search-field">

                            <label>
                                To Date
                            </label>

                            <input
                                type="date"
                                value={
                                    searchFilters.toDate
                                }
                                onChange={(e) =>
                                    setSearchFilters({
                                        ...searchFilters,
                                        toDate:
                                            e.target.value,
                                    })
                                }
                            />

                        </div>

                    </div>

                    <div className="search-actions">

                        <button
                            className="search-submit-btn"
                            onClick={handleSearch}
                        >
                            Search
                        </button>

                    </div>

                </div>
            )}

            {/* =========================
                TABLE
            ========================= */}
            <div className="table-container">

                <TableContainer
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "auto",
                        overflowY: "auto",

                        "&::-webkit-scrollbar": {
                            width: 8,
                            height: 8,
                        },

                        "&::-webkit-scrollbar-thumb": {
                            borderRadius: 20,
                            background: "#cbd5e1",
                        },

                        "&::-webkit-scrollbar-track": {
                            background: "#f8fafc",
                        },

                        /* 10-inch and larger screens */
                        "@media (min-width: 1025px)": {
                            overflowX: "hidden",
                        },
                    }}
                >
                    <Table
                        sx={{
                            width: "100%",
                            tableLayout: "fixed",

                            /* Smaller than 10-inch / smaller viewport */
                            "@media (max-width: 1024px)": {
                                minWidth: "1100px",
                            },

                            /* 10-inch and larger */
                            "@media (min-width: 1025px)": {
                                minWidth: 0,
                            },
                        }}
                    >
                        <TableHead
                            sx={{
                                "& th": {
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                    backgroundColor: "#F8FAFC",
                                },
                            }}
                        >
                            <TableRow>

                                {/* 1. Vendor Name */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "13%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Vendor Name
                                </TableCell>

                                {/* 2. Item */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "10%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                    }}
                                >
                                    Item
                                </TableCell>

                                {/* 3. Type */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "9%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Type
                                </TableCell>

                                {/* 4. Product ID */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "10%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Product ID
                                </TableCell>

                                {/* 5. QTY */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "7%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                    }}
                                >
                                    QTY
                                </TableCell>

                                {/* 6. Address */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "15%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                    }}
                                >
                                    Address
                                </TableCell>

                                {/* 7. Email */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "16%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                    }}
                                >
                                    Email
                                </TableCell>

                                {/* 8. Created By */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "9%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Created By
                                </TableCell>

                                {/* 9. Action */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "11%",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Action
                                </TableCell>

                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <TableRow key={index}>

                                        <TableCell align="center">
                                            <Skeleton width="80%" sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width="80%" sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width={70} sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width={70} sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width={40} sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width="80%" sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width="90%" sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Skeleton width={70} sx={{ mx: "auto" }} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    gap: 0.3,
                                                }}
                                            >
                                                <Skeleton
                                                    variant="circular"
                                                    width={32}
                                                    height={32}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={32}
                                                    height={32}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={32}
                                                    height={32}
                                                />
                                            </Box>
                                        </TableCell>

                                    </TableRow>
                                ))
                            ) : filteredOrders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        No Purchase Orders Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredOrders.map((order) => (
                                    <TableRow
                                        key={order.id}
                                        hover
                                        sx={{
                                            height: 64,

                                            "& td": {
                                                padding: {
                                                    xs: "10px 8px",
                                                    md: "14px 10px",
                                                },
                                                verticalAlign: "middle",
                                            },

                                            "&:hover": {
                                                backgroundColor: "#F8FBFF",
                                            },
                                        }}
                                    >

                                        {/* Vendor Name */}
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.vendorName || "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Item */}
                                        {/* <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.item ||
                                                    order.productName ||
                                                    "—"}
                                            </Typography>
                                        </TableCell> */}

                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.items?.[0]?.item ||
                                                    order.items?.[0]?.productName ||
                                                    order.item ||
                                                    order.productName ||
                                                    "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Type */}
                                        {/* <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.purchaseType ||
                                                    order.type ||
                                                    "—"}
                                            </Typography>
                                        </TableCell> */}

                                        <TableCell align="center">
                                            <span
                                                className={`purchase-type-badge ${(order.purchaseType || order.type || "").toLowerCase()
                                                    }`}
                                            >
                                                {order.purchaseType || order.type || "—"}
                                            </span>
                                        </TableCell>

                                        {/* Product ID */}
                                        {/* <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.productId || "—"}
                                            </Typography>
                                        </TableCell> */}

                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                {order.items?.[0]?.goodsItemId ||
                                                    order.items?.[0]?.productId ||
                                                    order.items?.[0]?.itemId ||
                                                    order.productId ||
                                                    "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* QTY */}
                                        {/* <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.quantity ??
                                                    order.qty ??
                                                    "—"}
                                            </Typography>
                                        </TableCell> */}

                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                {order.items?.[0]?.quantity ??
                                                    order.items?.[0]?.qty ??
                                                    order.quantity ??
                                                    order.qty ??
                                                    "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Address */}
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.address ||
                                                    order.vendorAddress ||
                                                    "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Email */}
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.email ||
                                                    order.vendorEmail ||
                                                    "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Created By */}
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {order.createdBy || "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Action */}
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: 0.3,
                                                    flexWrap: "nowrap",
                                                }}
                                            >

                                                <IconButton
                                                    onClick={() =>
                                                        handleView(order)
                                                    }
                                                    sx={{
                                                        color: "#2563EB",
                                                        width: {
                                                            xs: 30,
                                                            md: 36,
                                                        },
                                                        height: {
                                                            xs: 30,
                                                            md: 36,
                                                        },
                                                    }}
                                                >
                                                    <ViewIcon
                                                        sx={{
                                                            fontSize: {
                                                                xs: 16,
                                                                md: 20,
                                                            },
                                                        }}
                                                    />
                                                </IconButton>

                                                <IconButton
                                                    onClick={() =>
                                                        handleEdit(order.id)
                                                    }
                                                    sx={{
                                                        color: "#F59E0B",
                                                        width: {
                                                            xs: 30,
                                                            md: 36,
                                                        },
                                                        height: {
                                                            xs: 30,
                                                            md: 36,
                                                        },
                                                    }}
                                                >
                                                    <EditIcon
                                                        sx={{
                                                            fontSize: {
                                                                xs: 16,
                                                                md: 20,
                                                            },
                                                        }}
                                                    />
                                                </IconButton>

                                                <IconButton
                                                    onClick={() =>
                                                        handleDelete(order.id)
                                                    }
                                                    sx={{
                                                        color: "#EF4444",
                                                        width: {
                                                            xs: 30,
                                                            md: 36,
                                                        },
                                                        height: {
                                                            xs: 30,
                                                            md: 36,
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon
                                                        sx={{
                                                            fontSize: {
                                                                xs: 16,
                                                                md: 20,
                                                            },
                                                        }}
                                                    />
                                                </IconButton>

                                            </Box>
                                        </TableCell>

                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            </div>

        </div>
    );
}

export default PurchaseOrderList;